import { useState } from 'react'
import type { BackfillPreviewResponse } from '../types'
import {
  ACTION_TYPE_LABELS,
  SKIP_REASON_LABELS,
  CATEGORY_DISPLAY_NAMES,
  CATEGORY_COLORS,
} from '../types'

interface BackfillPreviewStepProps {
  preview: BackfillPreviewResponse
  isLoading: boolean
  error: string | null
  onConfirm: (backfillReason: string) => void
  onBack: () => void
}

export function BackfillPreviewStep({
  preview,
  isLoading,
  error,
  onConfirm,
  onBack,
}: BackfillPreviewStepProps) {
  const [reason, setReason] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  const handleConfirm = () => {
    if (!reason.trim()) {
      setValidationError('Debes ingresar un motivo de auditoría')
      return
    }
    setValidationError(null)
    onConfirm(reason.trim())
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-32 bg-gray-100 rounded-lg" />
        <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
        <button onClick={onBack} className="btn btn-secondary">
          Volver
        </button>
      </div>
    )
  }

  const colors = CATEGORY_COLORS[preview.category]

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${colors.bg} ${colors.border} border rounded-lg p-4`}>
          <p className="text-sm text-gray-600">Total clientes</p>
          <p className={`text-3xl font-bold ${colors.text}`}>
            {preview.total_clients.toLocaleString('es-CL')}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Nivel {CATEGORY_DISPLAY_NAMES[preview.category]}
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">Recibirán beneficios</p>
          <p className="text-3xl font-bold text-green-700">
            {preview.eligible_clients.toLocaleString('es-CL')}
          </p>
          <p className="text-xs text-gray-500 mt-1">clientes elegibles</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">Saltados</p>
          <p className="text-3xl font-bold text-gray-500">
            {preview.skipped_clients.toLocaleString('es-CL')}
          </p>
          <p className="text-xs text-gray-500 mt-1">por restricciones</p>
        </div>
      </div>

      {/* Per-rule breakdown */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Detalle por regla</h3>
        <div className="space-y-3">
          {preview.rules.map((rule) => {
            const skipEntries = Object.entries(rule.skip_reasons).filter(
              ([, count]) => count > 0,
            )
            return (
              <div key={rule.rule_id} className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{rule.rule_name}</span>
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      {ACTION_TYPE_LABELS[rule.action_type] ?? rule.action_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-green-600 font-medium">
                      {rule.eligible_count} elegibles
                    </span>
                    {rule.skipped_count > 0 && (
                      <span className="text-gray-400">
                        {rule.skipped_count} saltados
                      </span>
                    )}
                  </div>
                </div>
                {skipEntries.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {skipEntries.map(([reasonKey, count]) => (
                      <span
                        key={reasonKey}
                        className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500"
                      >
                        {SKIP_REASON_LABELS[reasonKey] ?? reasonKey}: {count}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* No emails notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="text-sm font-medium text-amber-800">Sin envío de emails</p>
          <p className="text-sm text-amber-700 mt-0.5">
            Los beneficios se asignarán sin enviar notificaciones por email a los clientes.
            Cada beneficio quedará marcado como retroactivo para auditoría.
          </p>
        </div>
      </div>

      {/* Audit reason */}
      <div>
        <label htmlFor="backfill-reason" className="block text-sm font-medium text-gray-700 mb-1">
          Motivo de asignación retroactiva *
        </label>
        <input
          id="backfill-reason"
          type="text"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value)
            if (validationError) setValidationError(null)
          }}
          placeholder="Ej: Asignación retroactiva nivel Oro - Marzo 2026"
          className={`input ${validationError ? 'border-red-300 focus:ring-red-500' : ''}`}
        />
        {validationError && (
          <p className="text-sm text-red-600 mt-1">{validationError}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <button onClick={onBack} className="btn btn-secondary">
          Volver
        </button>
        <button
          onClick={handleConfirm}
          disabled={preview.eligible_clients === 0}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirmar y ejecutar ({preview.eligible_clients} clientes)
        </button>
      </div>
    </div>
  )
}
